import React from 'react';
import styled from 'styled-components';
import MessageRenderer from './MessageRenderer';

function MessageRendererDemo() {
  const testMessages = [
    {
      title: "Basic Markdown",
      content: "This is **bold text**, this is *italic text*, and this is ***bold italic***.\n\nThis is a [link](https://example.com)."
    },
    {
      title: "Code Blocks",
      content: "Here's some inline `code` and a code block:\n\n```javascript\nfunction hello() {\n  console.log('Hello, world!');\n}\n```"
    },
    {
      title: "Lists",
      content: "Unordered list:\n- Item 1\n- Item 2\n- Item 3\n\nOrdered list:\n1. First\n2. Second\n3. Third"
    },
    {
      title: "Math Formulas - Inline",
      content: "The Pythagorean theorem is $a^2 + b^2 = c^2$. Einstein's famous equation is $E = mc^2$."
    },
    {
      title: "Math Formulas - Display",
      content: "Here's the quadratic formula:\n\n$$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$\n\nAnd the integral:\n\n$$\\int_{a}^{b} f(x) dx$$"
    },
    {
      title: "Complex Math",
      content: "Maxwell's equations:\n\n$$\\nabla \\cdot \\mathbf{E} = \\frac{\\rho}{\\epsilon_0}$$\n\n$$\\nabla \\times \\mathbf{E} = -\\frac{\\partial \\mathbf{B}}{\\partial t}$$"
    },
    {
      title: "Tables",
      content: "| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Row 1    | Data     | More     |\n| Row 2    | Data     | More     |"
    },
    {
      title: "Blockquote",
      content: "> This is a blockquote.\n> It can span multiple lines.\n>\n> - And contain other elements"
    },
    {
      title: "Headings",
      content: "# Heading 1\n## Heading 2\n### Heading 3\n\nRegular paragraph text."
    },
    {
      title: "Combined Example",
      content: "# AI Chat Example\n\nHere's how to solve a quadratic equation:\n\n1. Start with the standard form: $ax^2 + bx + c = 0$\n2. Use the quadratic formula:\n\n$$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$\n\n**Example:** Solve $x^2 - 5x + 6 = 0$\n\nWhere:\n- $a = 1$\n- $b = -5$\n- $c = 6$\n\n```python\nimport math\n\ndef solve_quadratic(a, b, c):\n    discriminant = b**2 - 4*a*c\n    x1 = (-b + math.sqrt(discriminant)) / (2*a)\n    x2 = (-b - math.sqrt(discriminant)) / (2*a)\n    return x1, x2\n\nprint(solve_quadratic(1, -5, 6))\n```\n\nThe solutions are $x = 2$ and $x = 3$."
    }
  ];

  return (
    <DemoContainer>
      <h1>MessageRenderer Demo</h1>
      <p>Demonstrating markdown and mathematical formula rendering capabilities</p>
      
      {testMessages.map((msg, idx) => (
        <MessageSection key={idx}>
          <MessageTitle>{msg.title}</MessageTitle>
          <MessageBox>
            <MessageRenderer content={msg.content} />
          </MessageBox>
        </MessageSection>
      ))}
    </DemoContainer>
  );
}

const DemoContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background-color: var(--bg-primary);
  
  h1 {
    color: var(--text-primary);
    margin-bottom: 0.5rem;
  }
  
  > p {
    color: var(--text-secondary);
    margin-bottom: 2rem;
  }
`;

const MessageSection = styled.div`
  margin-bottom: 2rem;
`;

const MessageTitle = styled.h3`
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
`;

const MessageBox = styled.div`
  background-color: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 1.5rem;
`;

export default MessageRendererDemo;
